import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, ShieldAlert, Clock, Lock } from 'lucide-react';
import { useAuthStore } from '../stores/auth';
import { useTranslation } from 'react-i18next';

// Declare Google reCAPTCHA globals
declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

// Utility function to format block time
const formatBlockTime = (seconds: number): string => {
  if (seconds < 60) {
    return `${seconds} секунд`;
  } else if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    return `${minutes} минут${minutes > 1 ? '' : 'у'}`;
  } else {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours} час${hours > 1 ? 'ов' : ''}${minutes > 0 ? ` и ${minutes} минут${minutes > 1 ? '' : 'у'}` : ''}`;
  }
};

export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login, isLoading, error, blockInfo } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY as string;

  // Load reCAPTCHA script
  useEffect(() => {
    // Only load the script if it hasn't been loaded yet
    if (!window.grecaptcha) {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${recaptchaSiteKey}`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
      
      return () => {
        document.head.removeChild(script);
      };
    }
  }, [recaptchaSiteKey]);

  const executeRecaptcha = async (): Promise<string> => {
    try {
      return await new Promise((resolve, reject) => {
        if (window.grecaptcha) {
          window.grecaptcha.ready(async () => {
            try {
              const token = await window.grecaptcha.execute(recaptchaSiteKey, { action: 'login' });
              resolve(token);
            } catch (error) {
              reject(error);
            }
          });
        } else {
          reject(new Error('reCAPTCHA not loaded'));
        }
      });
    } catch (error) {
      console.error('reCAPTCHA error:', error);
      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;

    try {
      // Get reCAPTCHA token before login
      const recaptchaToken = await executeRecaptcha();
      
      await login(email, password, recaptchaToken);
      navigate('/');
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  // Calculate the time when the account will be unblocked
  const calculateUnblockTime = () => {
    if (!blockInfo || blockInfo.block_time_remaining <= 0) return null;
    
    const now = new Date();
    const unblockTime = new Date(now.getTime() + blockInfo.block_time_remaining * 1000);
    return unblockTime.toLocaleTimeString();
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[#0D1016]">
      <div className="max-w-md w-full space-y-8 bg-[#1C2130] p-8 rounded-[30px] border border-[#2A3441]">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-[#2A3441] flex items-center justify-center">
            <MessageSquare className="h-8 w-8 text-[#00C0FF]" />
          </div>
          <h2 className="mt-6 text-3xl font-bold text-white font-['onest']">{t('login.title')}</h2>
          <p className="mt-2 text-[#64748B] font-['onest']">{t('login.subtitle')}</p>
        </div>

        {blockInfo && blockInfo.block_time_remaining > 0 && (
          <div className="p-4 rounded-xl bg-[#EF4444]/10 border border-[#EF4444]/30">
            <div className="flex items-center gap-2 text-[#EF4444] font-semibold mb-2">
              <ShieldAlert className="w-5 h-5" />
              <span>Temporary Block</span>
            </div>
            <div className="text-[#EF4444]/90 space-y-2">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 flex-shrink-0" />
                <span>Waiting time: <strong>{formatBlockTime(blockInfo.block_time_remaining)}</strong></span>
              </div>
              {calculateUnblockTime() && (
                <div className="text-sm">
                  Unblock in {calculateUnblockTime()}
                </div>
              )}
              <div className="text-sm mt-2">
                Blocked due to exceeding the number of login attempts from your IP address.
              </div>
            </div>
          </div>
        )}

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && !blockInfo && (
            <div className="text-center p-3 rounded-xl bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/20">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="sr-only">{t('login.email')}</label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading || !!blockInfo}
                className="appearance-none rounded-xl relative block w-full px-4 py-3 bg-[#0D1016] border border-[#2A3441] text-white placeholder-[#64748B] focus:outline-none focus:ring-2 focus:ring-[#00C0FF] focus:border-[#00C0FF] transition-colors disabled:opacity-50"
                placeholder={t('login.email')}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">{t('login.password')}</label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading || !!blockInfo}
                className="appearance-none rounded-xl relative block w-full px-4 py-3 bg-[#0D1016] border border-[#2A3441] text-white placeholder-[#64748B] focus:outline-none focus:ring-2 focus:ring-[#00C0FF] focus:border-[#00C0FF] transition-colors disabled:opacity-50"
                placeholder={t('login.password')}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading || !!blockInfo}
            className="w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-xl bg-[#00C0FF] text-white hover:bg-[#00C0FF]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#00C0FF] transition-colors disabled:opacity-50"
          >
            {isLoading ? (
              t('login.signing_in')
            ) : blockInfo && blockInfo.block_time_remaining > 0 ? (
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4" /> 
                <span>Account blocked</span>
              </div>
            ) : (
              t('login.sign_in')
            )}
          </button>
        </form>
      </div>
    </div>
  );
}